import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, Lock, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputClass = 'pl-10 h-12 rounded-xl bg-gray-100 border-gray-200 text-gray-900 placeholder:text-gray-400';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setError('Lien invalide'); setLoading(false); return; }
    axios.get(`${API}/auth/reset-password/${token}`)
      .then((res) => setEmail(res.data.data.email))
      .catch((err) => setError(err.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Le mot de passe doit contenir au moins 8 caractères'); return; }
    if (password !== confirmPassword) { toast.error('Les mots de passe ne correspondent pas'); return; }
    try {
      setSubmitting(true);
      await axios.post(`${API}/auth/reset-password-token`, { token, new_password: password });
      setDone(true);
      toast.success('Mot de passe réinitialisé !');
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-neon-pink animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="reset-password-error-page">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">Lien invalide</h2>
          <p className="text-gray-500 mb-6" data-testid="reset-password-error">{error}</p>
          <Button onClick={() => navigate('/login')} className="bg-neon-pink hover:bg-neon-pink/90 rounded-full" data-testid="reset-back-to-login-btn">
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="reset-password-success-page">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="font-heading text-2xl font-bold text-gray-900 mb-2">Mot de passe modifié</h2>
          <p className="text-gray-500 mb-6">Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.</p>
          <Button onClick={() => navigate('/login')} className="bg-neon-pink hover:bg-neon-pink/90 rounded-full" data-testid="reset-success-login-btn">
            Se connecter
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="Chef's Connect" className="w-16 h-16 rounded-2xl mx-auto mb-4" />
          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">Nouveau mot de passe</h1>
          <p className="text-gray-500">Pour le compte <span className="font-medium text-gray-900" data-testid="reset-password-email">{email}</span></p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-gray-200 rounded-2xl p-8 space-y-4">
          <div>
            <Label className="text-gray-700">Nouveau mot de passe</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 8 caractères" className={inputClass} data-testid="reset-password-input" autoFocus />
            </div>
          </div>
          <div>
            <Label className="text-gray-700">Confirmer le mot de passe</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Retapez votre mot de passe" className={inputClass} data-testid="reset-confirm-password-input" />
            </div>
          </div>

          <Button type="submit" disabled={submitting || password.length < 8 || password !== confirmPassword} className="w-full mt-2 bg-neon-pink hover:bg-neon-pink/90 rounded-full h-12 text-lg shadow-neon-pink text-gray-900 font-semibold" data-testid="reset-password-submit-btn">
            {submitting ? <Loader2 className="animate-spin mr-2" size={20} /> : <Check className="mr-2" size={20} />}
            Enregistrer
          </Button>

          <p className="text-center text-sm">
            <Link to="/login" className="text-gray-500 hover:text-neon-pink transition-colors" data-testid="reset-cancel-link">Retour à la connexion</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
